import React from 'react';

import {
  useInBreakpoints,
  Breakpoint,
} from 'js/design_system/hooks/useBreakpoint';

type Props = {
  in: Breakpoint[];
};

/**
 * Shows children only when the screen size is in the specified breakpoints.
 * NOTE: This component only displays or doesn't display the children, it doesn't do anything else.
 * If you need to do more complex breakpoint-based styling, use the `breakpoint` mixin from
 * `stylesheets/design_system/_breakpoint.scss` instead (in a styled component or stylesheet).
 * See that file for inline documentation on how to use the mixin.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Show } from 'js/design_system/Breakpoint';
 *
 * <Show in={['small', 'medium']}>
 *   <p>Only visible on small and medium screens</p>
 * </Show>
 * <Show in={['large']}>
 *   <p>Only visible on large screens</p>
 * </Show>
 * ```
 */
export const Show: React.FC<React.PropsWithChildren<Props>> = ({
  in: breakpoints,
  children,
}) => {
  const isInBreakpoints = useInBreakpoints(breakpoints);

  return isInBreakpoints ? <>{children}</> : null;
};
